import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../../Utils/httpClient';
import urlUtils from '../../Utils/urlUtils';
import { buildScreenPopLoginUrl, hasAttemptedLogin, toHubSpotRedirect, toScreenPopRequest } from '../../Utils/screenPopUtils';
import ScreenPopView from '../../Components/ScreenPop/ScreenPopView';
import type { ScreenPopResult, ScreenPopRootState, ScreenPopStatus } from './ScreenPop.types';

// getUserInfo only clears `authenticating` on success, 401 or a network failure; a 5xx or a hanging request leaves it set.
const AUTHENTICATION_TIMEOUT_MS = 15000;

// Landing page for Puzzel's CRM Pop. Rendered outside AppLayout so the agent only sees a short
// "creating call" view before being sent on to HubSpot.
const ScreenPopContainer = () => {
    const location = useLocation();
    const user = useSelector((state: ScreenPopRootState) => state.CommonReducer.user);
    const authenticating = useSelector((state: ScreenPopRootState) => state.CommonReducer.authenticating);
    const [status, setStatus] = useState<ScreenPopStatus>('authenticating');
    const [attempt, setAttempt] = useState(0);
    const startedAttemptRef = useRef<number | null>(null);

    const signIn = useCallback(() => {
        window.location.replace(buildScreenPopLoginUrl(urlUtils.getFullApiHostname(), location.pathname, location.search));
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!authenticating) return undefined;
        const timer = window.setTimeout(() => setStatus('error'), AUTHENTICATION_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [authenticating]);

    useEffect(() => {
        if (authenticating) return;

        if (user === null || user === undefined) {
            if (hasAttemptedLogin(location.search)) {
                setStatus('signInFailed');
            } else {
                setStatus('signingIn');
                signIn();
            }
            return;
        }

        // Each POST creates HubSpot records, so a re-render (e.g. the user object being refreshed)
        // must not fire it again; only an explicit retry bumps the attempt.
        if (startedAttemptRef.current === attempt) return;
        startedAttemptRef.current = attempt;

        setStatus('creating');
        apiFetch<ScreenPopResult>('/api/ScreenPop/Create', { method: 'POST', body: JSON.stringify(toScreenPopRequest(location.search)) })
            .then((result) => {
                const redirect = toHubSpotRedirect(result?.redirectUrl);
                if (redirect === null) throw new Error('Screen-pop returned no HubSpot redirect');
                window.location.replace(redirect);
            })
            .catch(() => setStatus('error'));
    }, [authenticating, user, location.search, signIn, attempt]);

    // Without a user lookup result there is nothing to retry in place; only the agent's click reloads, never a timer.
    const retry = () => (authenticating ? window.location.reload() : setAttempt((n) => n + 1));

    return (
        <main className="container pt-48">
            <ScreenPopView status={status} onSignIn={signIn} onRetry={retry} />
        </main>
    );
};

export default ScreenPopContainer;
